import { AppointmentInfo, ServiceID, ServiceInfo, WelcomeCall_ID, YCBMIntent } from "@imago/model"
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FatUserQuery } from "./user.ts"
import { useConfig } from "../api.ts"
import { useWelcomed } from "./user-emrs.ts"
import { YCBM_Intents_cancel, YCBM_Intents_confirm, YCBM_Intents_getSlots, YCBM_Intents_selectSlot, YCBM_Intents_selectTeamMember, YCBMRemote } from "@imago/api-client-ycbm"
import { Temporal, URIObject } from "@imago/api-client"

const writeToYCBM = true
const ycbm = new YCBMRemote(URIObject.parse('https://api.youcanbook.me'))

const AppointmentsQuery = {
	use() {
		const appointments = FatUserQuery.use().data?.appointments

		const config = useConfig()
		if (config.testMode) { // TODO remove
			const welcomed = useWelcomed()
			if (welcomed === true)
				return {data: appointments?.filter(app => app.serviceID != WelcomeCall_ID)}
		}
		return {data: appointments}
	},
	async add(qc: QueryClient, result: AppointmentInfo) {
		await FatUserQuery.patchData(qc, [], old => ({
			...old!,
			appointments: [
				...old!.appointments || [],
				result,
			]
		}))
	},
	async remove(qc: QueryClient, cached: AppointmentInfo) {
		await FatUserQuery.patchData(qc, [], old => ({
			...old!,
			appointments: old!.appointments!.filter(app => app.ycbm_secret != cached.ycbm_secret)
		}))
	},
	async invalidate(qc: QueryClient, t: []) {
		await FatUserQuery.invalidate(qc, [])
	},
}

export function useAppointments() {
	return AppointmentsQuery.use().data
}

export function useServiceAppointment(serviceID?: ServiceID) {
	const appointments = useAppointments()
	if (serviceID === undefined || appointments === undefined) return
	const filtered = appointments.filter(app => app.serviceID === serviceID)
	return filtered.length > 0 ? filtered[0] : null
}

const bookableSlotsMap = new Map<Promise<YCBMIntent>, Promise<Temporal.ZonedDateTime[]>>()

export function getBookableSlots(intent: Promise<YCBMIntent>) {
	if (!bookableSlotsMap.get(intent)) {
		bookableSlotsMap.set(intent, (async () => {
			const ycbmIntentID = (await intent).ycbm_secret
			return (await YCBM_Intents_getSlots(ycbm, ycbmIntentID)).map(slot => slot.toZonedDateTimeISO(Temporal.Now.timeZoneId()))
		})())
	}

	return bookableSlotsMap.get(intent)!
}

export function useBookMutation({service, intent}: {
	service: ServiceInfo
	intent: Promise<YCBMIntent>
}) {
	const isIntentBooked = useServiceAppointment(service.ID)
	if (isIntentBooked) throw new Error('Already booked')

	const qc = useQueryClient()
	const mutation = useMutation({
		mutationFn: async ({slot}: {
			slot: Temporal.ZonedDateTime
		}) => {
			const ycbmIntentID = (await intent).ycbm_secret
			try {
				if (writeToYCBM) {
					await YCBM_Intents_selectSlot(ycbm, ycbmIntentID, {
						startsAt: slot.toInstant().epochMilliseconds,
						timeZone: slot.timeZoneId,
					})
					await YCBM_Intents_selectTeamMember(ycbm, ycbmIntentID, {
						teamMemberId: "AUTO",
					})
					await YCBM_Intents_confirm(ycbm, ycbmIntentID)
				}

				const appointment: AppointmentInfo = {
					serviceID: service.ID,
					duration: service.duration,
					start: slot,
					end: slot.add(service.duration),
					ycbm_secret: ycbmIntentID
					// TODO googleMeetLink
				}
				AppointmentsQuery.add(qc, appointment)
				return appointment
			} finally {
				AppointmentsQuery.invalidate(qc, [])
			}
		}
	})
	if (isIntentBooked === undefined) return
	return mutation
}

export function useCancelBookingMutation({appointment}: {
	appointment: AppointmentInfo | undefined
}) {
	const ycbmIntentID = appointment?.ycbm_secret
	const isIntentBooked = useServiceAppointment(appointment?.serviceID)

	const qc = useQueryClient()
	const mutation = useMutation({
		mutationFn: async () => {
			try {
				if (writeToYCBM) {
					await YCBM_Intents_cancel(ycbm, ycbmIntentID!)
				}

				AppointmentsQuery.remove(qc, appointment!)
			} finally {
				AppointmentsQuery.invalidate(qc, [])
			}
		}
	})

	if (ycbmIntentID === undefined || isIntentBooked === undefined) return
	return isIntentBooked ? mutation : null
}
